import { flow, makeObservable, action, observable } from 'mobx';
import { GlobalStore, ApiResponse, FormStore } from '@roc/feature-app-core';
import { LoanStore } from '@roc/feature-loans';
import { DrawService } from '../../services/drawService';
import { PointOfContactService } from '../../services/pointOfContactService';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';

const pointOfContactForm = {
  fields: {
    pointOfContactId: {
      value: null,
      rule: '',
    },
    firstName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'First Name is a required field.',
    },
    lastName: {
      value: '',
      error: null,
      rule: 'required',
      message: 'Last Name is a required field.',
    },
    phone: {
      value: '',
      error: null,
      rule: 'required|regex:/^\\d{3}-?\\d{3}-?\\d{4}$/|alpha_dash',
      message: 'Phone must be a number with the format xxx-xxx-xxxx',
    },
    email: {
      value: '',
      error: null,
      rule: 'required|email',
    },
  },
  meta: {
    isValid: false,
    error: null,
  },
};

export class PointOfContactV2Store {
  private globalStore: GlobalStore;
  private drawService: DrawService;
  private pointOfContactService: PointOfContactService;

  pointOfContactFormStore: FormStore;

  pointOfContacts: any[] = [];

  constructor(globalStore) {
    this.globalStore = globalStore;
    this.drawService = new DrawService();
    this.pointOfContactFormStore = new FormStore(
      pointOfContactForm,
      globalStore
    );
    this.pointOfContactService = new PointOfContactService();
    makeObservable(this, {
      reset: action,
      initialize: flow,
      fetchPointOfContacts: flow,
    });
  }

  reset() {
    this.pointOfContactFormStore.reset();
    this.pointOfContacts = [];
  }

  *initialize(loanId: number) {
    this.reset();
    yield this.fetchPointOfContacts(loanId);
  }

  *fetchPointOfContacts(loanId: number) {
    try {
      const response = yield this.pointOfContactService.getPointOfContactsByLoanId(
        loanId
      );
      const data = response.data.data;
      this.pointOfContacts = data;
      const latestPointOfContact = [...this.pointOfContacts].sort(
        (a, b) => b.pointOfContactId - a.pointOfContactId
      )?.[0];
      if (latestPointOfContact) {
        this.loadPointOfContact(latestPointOfContact);
      }
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  selectPointOfContact(pointOfContactId: number) {
    this.pointOfContactFormStore.onFieldChange(
      'pointOfContactId',
      pointOfContactId
    );
    this.pointOfContactFormStore.reset();
    if (pointOfContactId) {
      const poc = this.pointOfContacts.find(
        poc => poc.pointOfContactId === pointOfContactId
      );
      this.pointOfContactFormStore.loadForm(poc);
    }
  }

  loadPointOfContact(pointOfContact: any) {
    this.pointOfContactFormStore.loadForm({
      pointOfContactId: pointOfContact.pointOfContactId,
      firstName: pointOfContact.firstName,
      lastName: pointOfContact.lastName,
      phone: pointOfContact.phone,
      email: pointOfContact.email,
      comments: '',
    });
  }

  async submit(loanId: number, taskIds: number[], propertyAddress: string) {
    try {
      if (!this.pointOfContactFormStore.runFormValidationWithMessage()) {
        return;
      }
      const values = this.pointOfContactFormStore.getFormValues();
      const response = await this.pointOfContactService.assignTask({
        pointOfContactId: values.pointOfContactId,
        loanId: loanId,
        propertyAddress: propertyAddress,
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone,
        email: values.email,
        comments: '',
        taskIds,
      });
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Task assigned successfully!',
      });
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }
}
